import React, { useState, useRef, useEffect } from 'react';

export const OTPInput = ({
  length = 4,
  onChange,
  disabled,
  otpError,
  focusTrigger = 0,
}) => {
  const [otp, setOtp] = useState(Array(length).fill(''));
  const [hasError, setHasError] = useState(false);
  const hiddenOtpRef = useRef(null);
  const inputRefs = useRef(
    Array(length)
      .fill(null)
      .map(() => React.createRef())
  );

  // Reset error state when OTP changes
  useEffect(() => {
    if (otp.some((digit) => digit !== '')) {
      setHasError(false);
    }
  }, [otp]);

  const focusInput = (index) => {
    if (index >= 0 && index < length) {
      inputRefs.current[index]?.current?.focus();
    }
  };

  const clearInputs = () => {
    if (hasError) {
      const newOtp = Array(length).fill('');
      setOtp(newOtp);
      onChange(newOtp.join(''));
      focusInput(0);
    }
  };

  const handleChange = (e, index) => {
    const rawDigits = e.target.value.replace(/\D/g, '');

    // Mobile OTP autofill often injects the full code into the focused input.
    // If we receive multiple digits at once, distribute across all boxes.
    if (rawDigits.length > 1) {
      applyOtpFromString(rawDigits);
      if (hiddenOtpRef.current) {
        hiddenOtpRef.current.value = rawDigits.slice(0, length);
      }
      return;
    }

    const value = rawDigits.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    onChange(newOtp.join(''));

    if (value && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otp];

      if (newOtp[index]) {
        newOtp[index] = '';
        setOtp(newOtp);
        onChange(newOtp.join(''));
      } else if (index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        onChange(newOtp.join(''));
        focusInput(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain');
    const pastedDigits = pastedData
      .replace(/\D/g, '')
      .slice(0, length)
      .split('');

    const newOtp = [...otp];
    pastedDigits.forEach((digit, index) => {
      if (index < length) {
        newOtp[index] = digit;
      }
    });

    setOtp(newOtp);
    onChange(newOtp.join(''));

    const nextEmptyIndex = newOtp.findIndex((val) => !val);
    focusInput(nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex);
  };

  // If there's an error and user clicks, clear all inputs
  const handleClick = (e) => {
    e.target.select();
    clearInputs();
  };

  const applyOtpFromString = (rawValue = '') => {
    const digits = rawValue.replace(/\D/g, '').slice(0, length).split('');
    const newOtp = Array(length)
      .fill('')
      .map((_, index) => digits[index] || '');

    setOtp(newOtp);
    onChange(newOtp.join(''));

    const nextEmptyIndex = newOtp.findIndex((val) => !val);
    focusInput(nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex);
  };

  const handleHiddenOtpChange = (e) => {
    applyOtpFromString(e.target.value);
  };

  // Set error state from parent through useEffect
  useEffect(() => {
    if (otpError) {
      setHasError(true);
    }
  }, [otpError]);

  // On OTP send/resend, clear existing digits and focus the first input.
  // IMPORTANT: do NOT clear when `disabled` flips during verification,
  // otherwise autofill-filled digits disappear from the UI.
  useEffect(() => {
    const newOtp = Array(length).fill('');
    setOtp(newOtp);
    onChange(newOtp.join(''));

    focusInput(0);
    if (hiddenOtpRef.current) {
      hiddenOtpRef.current.value = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTrigger, length]);

  return (
    <div className="flex gap-3 mt-2 justify-center relative">
      {/* Hidden field to improve iOS/Android one-time-code autofill reliability */}
      <input
        ref={hiddenOtpRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        name="one-time-code"
        disabled={disabled}
        onChange={handleHiddenOtpChange}
        aria-label="One time code"
        style={{
          position: 'absolute',
          opacity: 0,
          pointerEvents: 'none',
          width: 1,
          height: 1,
        }}
      />
      {otp.map((digit, index) => {
        const inputId = `otp-input-${index}`;

        // Border: error → filled → empty.
        let boxBorder = '1.5px solid var(--ah-border-card-light)';
        if (hasError) boxBorder = '1.5px solid var(--ah-status-error)';
        else if (digit) boxBorder = '1.5px solid var(--ah-brand-primary)';

        // Background, theme-aware: --surface is #fff in light / dark slate in
        // dark, so a filled box no longer traps light heading text on white.
        let boxBackground = 'var(--ah-surface-input)';
        if (disabled) boxBackground = 'var(--surface-muted)';
        else if (digit) boxBackground = 'var(--surface)';

        return (
          <input
            key={inputId}
            id={inputId}
            ref={inputRefs.current[index]}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            // Let the first box receive full OTP from mobile autofill.
            // We'll immediately split it across boxes in `handleChange`.
            maxLength={index === 0 ? length : 1}
            value={digit}
            disabled={disabled}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            onClick={handleClick}
            style={{
              width: 48,
              height: 50,
              textAlign: 'center',
              fontSize: '1.25rem',
              fontWeight: 600,
              fontFamily: "'Sora', sans-serif",
              border: boxBorder,
              borderRadius: 10,
              background: boxBackground,
              color: 'var(--ah-text-heading)',
              outline: 'none',
              transition: 'all 0.2s ease',
              cursor: disabled ? 'not-allowed' : 'text',
              flexShrink: 0,
            }}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            aria-label={`OTP digit ${index + 1}`}
          />
        );
      })}
    </div>
  );
};
